import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Provider, useDispatch, useSelector } from "react-redux";
import { SlotFillProvider, Slot } from '@wordpress/components';
import { PluginArea } from '@wordpress/plugins';
import { applyFilters } from "@wordpress/hooks";

// CSS
import "./css/add-product.css";
import "./css/customers.css";
import "./css/store-menu.css";
import "./css/store-products.css";
import "./css/main-menu.css";

// toaster
import RootToast from "../common/RootToast";

// Redux
import { store } from "./redux-store/store";
import { setCurrentTab } from "./redux-store/slices/currentTab";

// Layout
import MainLayout from "../common/components/MainLayout";
import Notices from "../common/components/Notices/Notices";

// Pages
import Dashboard from "./pages/Dashboard";
import Products from "./pages/Products";
import AddProduct from "./pages/Products/AddProduct";
import EditProduct from "./pages/Products/EditProduct";
import Attributes from "./pages/Attributes";
import Categories from "./pages/Categories";
import Tags from "./pages/Tags";
import Brands from "./pages/Brands";
import Orders from "./pages/Orders";
import NewOrder from "./pages/Orders/NewOrder";
import SingleOrder from "./pages/Orders/SingleOrder";
import Transactions from "./pages/Transactions";
import Customers from "./pages/Customers";
import SingleCustomer from "./pages/Customers/SingleCustomer";
import Reports from "./pages/Reports";
import Coupons from "./pages/Coupons";
import CouponDetails from "./pages/Coupons/CouponDetails";
import AbandonedCart from "./pages/AbandonedCart";
import ProductReviews from "./pages/ProductReviews";
import Help from "./pages/Help";
import Addons from "./pages/Addons";
import Pro from "./pages/Pro";
import Refunds from "./pages/Refunds";

const getPageFromPath = (path) => {
    const pageRegex = /\/page\/(\d+)$/;
    const match = path.match(pageRegex);
    return match ? Number(match[1]) : 1;
};

const App = () => {
    const dispatch = useDispatch();
    const currentTab = useSelector((state) => state.currentTab.active);

    const [isLoading, setIsLoading] = useState(true);
    const [productView, setProductView] = useState(null);
    const [orderId, setOrderId] = useState(null);
    const [customerId, setCustomerId] = useState(null);
    const [page, setPage] = useState(1);
    const [couponView, setCouponView] = useState(null);
    const [productFilterId, setProductFilterId] = useState(null);

    // For dynamic breadcrumb title
    const [editProductTitle, setEditProductTitle] = useState('');
    const [editCouponTitle, setEditCouponTitle] = useState('');
    const [customerName, setCustomerName] = useState('');
   
    useEffect(() => {
        const updateBackendMenu = () => {
            const wrapper = document.querySelector('.toplevel_page_easycommerce');
            if (!wrapper) return;

            const allLi = wrapper.querySelectorAll('.wp-submenu li');
            const allLinks = wrapper.querySelectorAll('.wp-submenu a');

            allLi.forEach(li => li.classList.remove('current'));
            allLinks.forEach(a => a.classList.remove('current'));

            let normalizedTab = currentTab || "/dashboard";

            const routeMapper = [
                { match: "/products/add", to: "/products/add" },
                { match: "/products/edit", to: "/products/edit" },
                { match: "/products", to: "/products" },
                { match: "/attributes", to: "/attributes" },
                { match: "/categories", to: "/categories" },
                { match: "/tags", to: "/tags" },
                { match: "/brands", to: "/brands" },
                { match: "/orders", to: "/orders" },
                { match: "/refunds", to: "/refunds" },
                { match: "/customers", to: "/customers" },
                { match: "/coupons", to: "/coupons" },
                { match: "/transactions", to: "/transactions" },
                { match: "/reports", to: "/reports" },
                { match: "/abandoned-cart", to: "/abandoned-cart" },
                { match: "/reviews", to: "/reviews" },
                { match: "/licenses", to: "/licenses" },
                { match: "/subscriptions", to: "/subscriptions" },
                { match: "/settings", to: "easycommerce-settings" },
                { match: "/dashboard", to: "/dashboard" }, 
                { match: "/get-pro", to: "/get-pro" },
                { match: "/help", to: "/help" },
                { match: "/addons", to: "/addons" },
            ];

            let searchHash = "";

            for (const item of routeMapper) {
                if (normalizedTab.startsWith(item.match)) {
                    searchHash = item.to;
                    break;
                }
            }

            const links = wrapper.querySelectorAll('.wp-submenu a');

            links.forEach(link => {
                const href = link.getAttribute('href');
                const li = link.closest('li');
                if (!href || !li) return;

                if (href === "admin.php?page=easycommerce-settings") {
                    if (normalizedTab === "/settings") {
                        li.classList.add("current");
                        link.classList.add("current");
                    }
                    return;
                }

                if (href === "admin.php?page=easycommerce" || href === "admin.php?page=easycommerce#") {
                    if (normalizedTab === "/dashboard") {
                        li.classList.add("current");
                        link.classList.add("current");
                    }
                    return;
                }

                if (href.includes("#")) {
                    const hash = href.split("#")[1]; 

                    if (hash === searchHash) {
                        li.classList.add("current");
                        link.classList.add("current");
                    }
                }
            });
            
        };

        updateBackendMenu();
    }, [currentTab]);


    const handleHashChange = () => {
        const hash = window.location.hash.replace("#", "");
        const [hashPath] = hash.split("/page/");
        const currentPage = getPageFromPath(hash);

        const resetState = () => {
            setProductView(null);
            setOrderId(null);
            setCustomerId(null);
            setCouponView(null);
            setEditProductTitle('');
        };

        const routeConfig = {
            "": () => {  
                dispatch(setCurrentTab("/dashboard"));
                resetState();
            },
            "/dashboard": () => {
                dispatch(setCurrentTab("/dashboard"));
                resetState();
            },
            "/products": () => {
                dispatch(setCurrentTab("/products"));
                setProductView(null);
                setPage(currentPage);
            },
            "/products/add": () => {
                dispatch(setCurrentTab("/products/add"));
                setProductView("add");
            },
            "/attributes": () => {
                dispatch(setCurrentTab("/attributes"));
                setPage(currentPage);
            },
            "/categories": () => {
                dispatch(setCurrentTab("/categories"));
                setPage(currentPage);
            },
            "/tags": () => {
                dispatch(setCurrentTab("/tags"));
                setPage(currentPage);
            },
            "/brands": () => {
                dispatch(setCurrentTab("/brands"));
                setPage(currentPage);
            },
            "/orders": () => {
                dispatch(setCurrentTab("/orders"));
                setOrderId(null);
                setProductFilterId(null);
                setPage(currentPage);
            },
            "/refunds": () => {
                dispatch(setCurrentTab("/refunds"));
                setPage(currentPage);
            },
            ordersByProduct: (match) => {
                dispatch(setCurrentTab("/orders"));
                setOrderId(null);
                setProductFilterId(match[1]);
                setPage(currentPage);
            },
            "/transactions": () => {
                dispatch(setCurrentTab("/transactions"));
                setPage(currentPage);
            },
            "/customers": () => {
                dispatch(setCurrentTab("/customers"));
                setCustomerId(null);
                setPage(currentPage);
            },
            "/abandoned-cart": () => {
                dispatch(setCurrentTab("/abandoned-cart"));
                setPage(currentPage);
            },
            "/reviews": () => {
                dispatch(setCurrentTab("/reviews"));
                setPage(currentPage);
            },
            "/coupons": () => {
                dispatch(setCurrentTab("/coupons"));
                setCouponView(null);
                setPage(currentPage);
            },
            "/coupons/new": () => {
                dispatch(setCurrentTab("/coupons"));
                setCouponView({ mode: "add" });
            },
            "/reports": () => {
                dispatch(setCurrentTab("/reports"));
            },
            "/help": () => {
                dispatch(setCurrentTab("/help"));
            },
            "/addons": () => {
                dispatch(setCurrentTab("/addons"));
            },
            "/get-pro": () => {
                dispatch(setCurrentTab("/get-pro"));
            },
            // Dynamic
            editProduct: (match) => {
                dispatch(setCurrentTab("/products"));
                setProductView({ mode: "edit", id: match[1] });
            },
            singleOrder: (match) => {
                dispatch(setCurrentTab("/orders"));
                setOrderId(match[1]);
            },
            singleCustomer: (match) => {
                dispatch(setCurrentTab("/customers"));
                setCustomerId(match[1]);
            },
            editCoupon: (match) => {
                dispatch(setCurrentTab("/coupons"));
                setCouponView({ mode: "edit", id: match[1] });
            },
        };

        
        if (routeConfig[hashPath]) {
            routeConfig[hashPath]();
        } else {
            const matchers = [
                [routeConfig.editProduct, /^\/products\/edit\/(\d+)$/],
                [routeConfig.singleOrder, /^\/orders\/(\d+)$/],
                [routeConfig.singleCustomer, /^\/customers\/(\d+)$/],
                [routeConfig.editCoupon, /^\/coupons\/edit\/(\d+)$/],
                [routeConfig.ordersByProduct, /^\/orders\/product\/(\d+)$/],
            ];

            let matched = false;
            for (const [handler, regex] of matchers) {
                const match = hashPath.match(regex);
                if (match) {
                    handler(match);
                    matched = true;
                    break;
                }
            }

            if (!matched) {
                dispatch(setCurrentTab(hashPath || "/dashboard"));
            }
        }

        window.scrollTo(0, 0);
        setIsLoading(false);
    };

    useEffect(() => {
        window.addEventListener("hashchange", handleHashChange);
        handleHashChange();

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, []);

    const breadcrumbMap = applyFilters("easycommerce.store.breadcrumbMap", {
        "/dashboard": ["EasyCommerce", "Dashboard"], 
        "/products": productView?.mode === "edit"
            ? ["EasyCommerce", "Products", editProductTitle || "Edit"]
            : ["EasyCommerce", "Products"],
        "/products/add": ["EasyCommerce", "Products", "Add Product"],
        "/attributes": ["EasyCommerce", "Products", "Attributes"],
        "/categories": ["EasyCommerce", "Products", "Categories"],
        "/tags": ["EasyCommerce", "Products", "Tags"],
        "/brands": ["EasyCommerce", "Products", "Brands"],
        "/orders": orderId ? ["EasyCommerce", "Orders", `#${orderId}`] : ["EasyCommerce", "Orders"],
        "/orders/new": ["EasyCommerce", "Orders", "New Order"],
        "/refunds": ["EasyCommerce", "Refunds"],
        "/transactions": ["EasyCommerce", "Transactions"],
        "/customers": customerId ? ["EasyCommerce", "Customers", `#${customerName}`] : ["EasyCommerce", "Customers"],
        "/coupons": couponView?.mode === "edit"
        ? ["EasyCommerce", "Coupons", editCouponTitle || "Edit Coupon"]
        : couponView?.mode === "add"
        ? ["EasyCommerce", "Coupons", "Add Coupon"]
        : ["EasyCommerce", "Coupons"],
        "/reports": ["EasyCommerce", "Reports"],
        "/abandoned-cart": ["EasyCommerce", "Abandoned Cart"],
        "/reviews": ["EasyCommerce", "Product Reviews"],
        "/addons": ["EasyCommerce", "Addons"],
        "/help": ["EasyCommerce", "Help & Support"],
        "/get-pro": ["EasyCommerce", "Pro"],
    });

    const renderContent = () => {
        let PageComponent = null;

        switch (currentTab) {
            case "/dashboard": 
                PageComponent = Dashboard;
                break;
            case "/products":
                if (productView?.mode === "edit") {
                    PageComponent = () => (
                        <EditProduct
                            id={productView.id}
                            setBreadcrumbTitle={setEditProductTitle}
                        />
                    );
                } else {
                    PageComponent = () => <Products page={page} />;
                }
                break;
            case "/products/add":
                PageComponent = AddProduct;
                break;
            case "/attributes":
                PageComponent = () => <Attributes page={page} />;
                break;
            case "/categories":
                PageComponent = () => <Categories page={page} />;
                break;
            case "/tags":
                PageComponent = () => <Tags page={page} />;
                break;
            case "/brands":
                PageComponent = () => <Brands page={page} />;
                break;
            case "/orders":
                PageComponent = orderId ? () => <SingleOrder id={orderId} /> : () => <Orders page={page} productId={productFilterId} />;
                break;
            case "/refunds":
                PageComponent = () => <Refunds page={page} />;
                break;
            case "/transactions":
                PageComponent = () => <Transactions page={page} />;
                break;
            case "/customers":
                PageComponent = customerId ? () => <SingleCustomer id={customerId} page={page} setBreadcrumbTitle={setCustomerName} /> : () => <Customers page={page} />;
                break;
            case "/coupons":
                if (couponView?.mode === "add") {
                    PageComponent = () => <CouponDetails setBreadcrumbTitle={() => {}} />;
                }
                else if (couponView?.mode === "edit") {
                    PageComponent = () => <CouponDetails id={couponView.id} setBreadcrumbTitle={setEditCouponTitle}/>;
                } else {
                    PageComponent = () => <Coupons page={page} />;
                }
                break;
            case "/reports":
                PageComponent = Reports;
                break;
            case "/abandoned-cart":
                PageComponent = () => <AbandonedCart page={page} />;
                break;
            case "/reviews":
                PageComponent = () => <ProductReviews page={page} />;
                break;
            case "/help":
                PageComponent = () => <Help page={page} />;
                break;
            case "/addons":
                PageComponent = () => <Addons page={page} />;
                break;
            case "/get-pro":
                PageComponent = () => <Pro page={page} />;
                break;
        }

        const breadcrumb = breadcrumbMap[currentTab];

        return (
            <MainLayout breadcrumb={breadcrumb}>
                
                <div>
                    <Notices />
                </div>

                <Slot name="easycommerce.beforeMainContent" fillProps={{ currentTab }} />
                    {PageComponent && <PageComponent />}
                <Slot name="easycommerce.afterMainContent" fillProps={{ currentTab }} />
            </MainLayout>
        );
    };

    return (
        <div className="bg-[#EEF0FF]">
            {isLoading ? <div>Loading...</div> : renderContent()}
            <RootToast />
        </div>
    );
};

const container = document.getElementById("easycommerce_render");
const root = createRoot(container);
root.render(
    <Provider store={store}>
        <SlotFillProvider>
            <PluginArea scope="easycommerce" />
            <App />
        </SlotFillProvider>
    </Provider>
);

export default App;